import React, { HTMLAttributes, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Icon } from '@evertel/web/ui'
import { getTextColor, getClosestVersionByHeight } from '@evertel/utils'
import { APIDataDocumentSchema } from '@evertel/types'
import { IconName } from '@fortawesome/fontawesome-svg-core'

interface DocumentIconProps extends HTMLAttributes<HTMLElement> {
    schema: APIDataDocumentSchema
    width?: number
    height?: number
    className?: string
}

const DocumentIcon: React.FC<DocumentIconProps> = ({
    schema,
    width = 20,
    height = 20,
    className,
    ...otherProps
}) => {

    // request the smallest resized version that covers the box we're drawing into,
    // rather than the full-size default the server would otherwise serve.
    // Falls back to the raw url for local previews and for media whose resized
    // versions haven't been generated yet (processing is async post-upload)
    const iconUrl = useMemo(() => {
        const iconMedia = schema?.iconMedia
        if (!iconMedia?.url) return undefined

        return getClosestVersionByHeight(height, iconMedia)?.url || iconMedia.url
    }, [schema?.iconMedia, height])

    // tracking the url that failed (rather than a boolean) means a newly picked
    // image automatically gets a fresh attempt
    const [failedUrl, setFailedUrl] = useState<string>()

    if (!schema) return null

    // an uploaded image and a glyph icon are mutually exclusive. Fall back to the
    // glyph if the image can't be loaded
    if (iconUrl && failedUrl !== iconUrl) {
        return (
            <img
                src={iconUrl}
                className={classNames('profile-pic flex-shrink-0', className)}
                width={width}
                height={height}
                alt=""
                onError={() => setFailedUrl(iconUrl)}
                {...otherProps}
            />
        )
    }

    return (
        <Icon
            name={schema.icon as IconName}
            className={classNames('document-icon flex-shrink-0', className)}
            style={{ background: schema.color, width, height }}
            faProps={{ title: `Document Icon: ${schema.icon}` }}
            color={(getTextColor(schema.color as string))}
            fixedWidth={true}
            size={height * 0.6}
            {...otherProps}
        />
    )
}

export {
    DocumentIcon
}
